// A pooled board sprite (IMPLEMENTATION §2.9 "Sprites are pooled"): one Phaser Image plus the values it
// shows, so a per-frame sync only calls Phaser when something changed. Pools never destroy images; a released
// sprite is hidden and reused. No per-frame Text or Graphics exist anywhere on the board.
import type { FrameIndex } from './frames.ts';
import {
  addImage,
  clearCrop,
  clearTint,
  crop,
  isCropped,
  type PImage,
  type PScene,
  setAlpha,
  setAngle,
  setBlendAdd,
  setDepth,
  setDisplaySize,
  setFlipX,
  setOriginAt,
  setPos,
  setTextureFrame,
  setVisible,
  tintFill,
  tintMultiply,
} from './phaserCompat.ts';

export class Spr {
  readonly img: PImage;
  private frame = '';
  private x = Number.NaN;
  private y = Number.NaN;
  private w = Number.NaN;
  private h = Number.NaN;
  private a = 1;
  private depth = Number.NaN;
  private shown = false;
  private tint = -1;
  private tintFillMode = false;
  private cropW = -1;
  private deg = 0;
  private flipped = false;
  private additive = false;

  constructor(img: PImage) {
    this.img = img;
    setVisible(img, false);
  }

  show(on: boolean): void {
    if (on !== this.shown) {
      this.shown = on;
      setVisible(this.img, on);
    }
  }

  get visible(): boolean {
    return this.shown;
  }

  /** The frame id it shows (diagnostics). */
  get frameName(): string {
    return this.frame;
  }

  setFrame(frames: FrameIndex, id: string): void {
    if (id === this.frame) return;
    // Phaser keeps a crop across setFrame (TextureCrop re-applies it to the new frame): drop it first, or the
    // new frame is drawn clipped to the old crop rect (a released progress bar reused for an item).
    this.cropLeft(-1, 0);
    this.frame = id;
    const f = frames.get(id);
    setTextureFrame(this.img, f.key, f.frame);
    this.w = Number.NaN; // the display size must be re-applied for the new frame
  }

  /** Centre position and display size in world units. */
  place(x: number, y: number, w: number, h: number): void {
    if (x !== this.x || y !== this.y) {
      this.x = x;
      this.y = y;
      setPos(this.img, x, y);
    }
    if (w !== this.w || h !== this.h) {
      this.w = w;
      this.h = h;
      setDisplaySize(this.img, w, h);
    }
  }

  /** Rotation in degrees, clockwise (the drill head overlay; 0 for everything else). */
  angle(deg: number): void {
    if (deg !== this.deg) {
      this.deg = deg;
      setAngle(this.img, deg);
    }
  }

  /** Mirror in x before the rotation (a left-turning corner chute's chevrons; false for everything else). */
  flip(on: boolean): void {
    if (on !== this.flipped) {
      this.flipped = on;
      setFlipX(this.img, on);
    }
  }

  pos(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  setDepth(d: number): void {
    if (d !== this.depth) {
      this.depth = d;
      setDepth(this.img, d);
    }
  }

  alpha(a: number): void {
    if (a !== this.a) {
      this.a = a;
      setAlpha(this.img, a);
    }
  }

  /** Additive blending (M10 flashes, sparks, glows). */
  blend(add: boolean): void {
    if (add !== this.additive) {
      this.additive = add;
      setBlendAdd(this.img, add);
    }
  }

  /** Multiply tint (−1 = none); `fill` replaces the colour instead (M6 input glows on the ring frame). */
  setTint(color: number, fill = false): void {
    if (color === this.tint && fill === this.tintFillMode) return;
    this.tint = color;
    this.tintFillMode = fill;
    if (color < 0) clearTint(this.img);
    else if (fill) tintFill(this.img, color);
    else tintMultiply(this.img, color);
  }

  /** Crops to the left `px` source pixels of the frame (−1 = no crop). */
  cropLeft(px: number, frameH: number): void {
    if (px === this.cropW) return;
    this.cropW = px;
    if (px < 0) clearCrop(this.img);
    else crop(this.img, 0, 0, px, frameH);
  }
}

/** A free list of sprites that share an origin; `active` counts the ones handed out. */
export class SprPool {
  private readonly free: Spr[] = [];
  private readonly all: Spr[] = [];
  private live = 0;
  private made = 0;

  private readonly scene: PScene;
  private readonly frames: FrameIndex;
  private readonly origin: { x: number; y: number };

  constructor(
    scene: PScene,
    frames: FrameIndex,
    origin: { x: number; y: number } = { x: 0.5, y: 0.5 },
  ) {
    this.scene = scene;
    this.frames = frames;
    this.origin = origin;
  }

  acquire(frame: string, depth: number): Spr {
    let s = this.free.pop();
    if (!s) {
      const f = this.frames.get(frame);
      const img = addImage(this.scene, 0, 0, f.key, f.frame);
      setOriginAt(img, this.origin.x, this.origin.y);
      s = new Spr(img);
      this.all.push(s);
      this.made += 1;
    }
    s.setFrame(this.frames, frame);
    s.cropLeft(-1, 0); // a sprite leaves the pool uncropped even when it keeps its frame
    s.angle(0);
    s.flip(false);
    s.setDepth(depth);
    s.alpha(1);
    s.setTint(-1);
    s.blend(false);
    s.show(true);
    this.live += 1;
    return s;
  }

  /** Changes the frame of a sprite from this pool. */
  frame(s: Spr, frame: string): void {
    s.setFrame(this.frames, frame);
  }

  release(s: Spr): void {
    s.show(false);
    s.cropLeft(-1, 0);
    this.free.push(s);
    this.live -= 1;
  }

  get active(): number {
    return this.live;
  }

  /**
   * Images whose Phaser crop is set although they are not a shown sprite of `croppable` (diagnostics: a crop
   * that leaked through the pool draws the new frame clipped, i.e. effectively invisible).
   */
  strayCrops(croppable: (frame: string) => boolean): number {
    let n = 0;
    for (const s of this.all) if (isCropped(s.img) && !(s.visible && croppable(s.frameName))) n++;
    return n;
  }

  /** Images ever created by this pool (active + free). */
  get created(): number {
    return this.made;
  }
}
